import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Plus, 
  Trash2, 
  DollarSign, 
  CreditCard, 
  FileText, 
  Calendar,
  Repeat,
  ArrowRight
} from "lucide-react";

/**
 * PricingSection - Comprehensive payment configuration component
 * 
 * Supports:
 * - One-time payment
 * - Split payments (2-3 installments)
 * - Subscription (monthly/quarterly/annual)
 * - Hybrid (one-time + subscription)
 * 
 * Delivery methods:
 * - Payment Link (Stripe Checkout)
 * - Invoice (Stripe Invoice)
 */
const PricingSection = ({ section, updateSection, readOnly = false }) => {
  const content = section.content || {};
  
  // Payment structure type
  const paymentType = content.payment_type || "one_time";
  
  // Delivery method
  const deliveryMethod = content.delivery_method || "payment_link";
  
  // Calculate totals for display
  const calculateOneTimeTotal = () => {
    if (paymentType === "one_time") {
      return (content.items || []).reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    }
    if (paymentType === "split") {
      return (content.split_payments || []).reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    }
    if (paymentType === "hybrid") {
      return parseFloat(content.onboarding_fee) || 0;
    }
    return 0;
  };

  const calculateRecurringTotal = () => {
    if (paymentType === "subscription" || paymentType === "hybrid") {
      return parseFloat(content.subscription?.amount) || 0;
    }
    return 0;
  };

  const formatFrequency = (freq) => {
    switch (freq) {
      case "monthly": return "/month";
      case "quarterly": return "/quarter";
      case "annually": return "/year";
      default: return "";
    }
  };

  const update = (changes) => {
    updateSection(section.id, changes);
  };

  return (
    <div className="space-y-6">
      {/* Payment Structure Selection */}
      {!readOnly && (
        <div className="space-y-3">
          <Label className="text-sm font-medium">Payment Structure</Label>
          <RadioGroup
            value={paymentType}
            onValueChange={(value) => update({ payment_type: value })}
            className="grid grid-cols-2 gap-3"
          >
            <Label
              htmlFor="one_time"
              className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                paymentType === "one_time" ? "border-forest bg-forest/5" : "border-stone hover:border-forest/50"
              }`}
            >
              <RadioGroupItem value="one_time" id="one_time" />
              <div>
                <p className="font-medium text-sm">One-Time</p>
                <p className="text-xs text-muted-foreground">Single payment</p>
              </div>
            </Label>
            
            <Label
              htmlFor="split"
              className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                paymentType === "split" ? "border-forest bg-forest/5" : "border-stone hover:border-forest/50"
              }`}
            >
              <RadioGroupItem value="split" id="split" />
              <div>
                <p className="font-medium text-sm">Split Payments</p>
                <p className="text-xs text-muted-foreground">2-3 installments</p>
              </div>
            </Label>
            
            <Label
              htmlFor="subscription"
              className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                paymentType === "subscription" ? "border-forest bg-forest/5" : "border-stone hover:border-forest/50"
              }`}
            >
              <RadioGroupItem value="subscription" id="subscription" />
              <div>
                <p className="font-medium text-sm">Subscription</p>
                <p className="text-xs text-muted-foreground">Recurring billing</p>
              </div>
            </Label>
            
            <Label
              htmlFor="hybrid"
              className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                paymentType === "hybrid" ? "border-forest bg-forest/5" : "border-stone hover:border-forest/50"
              }`}
            >
              <RadioGroupItem value="hybrid" id="hybrid" />
              <div>
                <p className="font-medium text-sm">Setup + Subscription</p>
                <p className="text-xs text-muted-foreground">One-time fee + recurring</p>
              </div>
            </Label>
          </RadioGroup>
        </div>
      )}

      {/* Read-only badge */}
      {readOnly && (
        <Badge variant="outline" className="mb-2">
          {paymentType === "one_time" && "One-Time Payment"}
          {paymentType === "split" && "Split Payments"}
          {paymentType === "subscription" && "Subscription"}
          {paymentType === "hybrid" && "Setup Fee + Subscription"}
        </Badge>
      )}

      {/* ONE-TIME PAYMENT */}
      {paymentType === "one_time" && (
        <div className="space-y-4">
          <Label className="text-sm font-medium">Line Items</Label>
          {(content.items || [{ description: "", amount: 0 }]).map((item, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <Input
                value={item.description}
                onChange={(e) => {
                  const newItems = [...(content.items || [])];
                  newItems[idx] = { ...newItems[idx], description: e.target.value };
                  update({ items: newItems });
                }}
                placeholder="Service description"
                className="flex-1"
                disabled={readOnly}
              />
              <div className="relative w-32">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  value={item.amount || ""}
                  onChange={(e) => {
                    const newItems = [...(content.items || [])];
                    newItems[idx] = { ...newItems[idx], amount: parseFloat(e.target.value) || 0 };
                    update({ items: newItems });
                  }}
                  className="pl-7"
                  disabled={readOnly}
                />
              </div>
              {!readOnly && (content.items || []).length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    const newItems = (content.items || []).filter((_, i) => i !== idx);
                    update({ items: newItems });
                  }}
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              )}
            </div>
          ))}
          {!readOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const newItems = [...(content.items || []), { description: "", amount: 0 }];
                update({ items: newItems });
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Line Item
            </Button>
          )}
        </div>
      )}

      {/* SPLIT PAYMENTS */}
      {paymentType === "split" && (
        <div className="space-y-4">
          <Label className="text-sm font-medium">Payment Schedule</Label>
          {(content.split_payments || [
            { amount: 0, due_description: "Upon signing", send_timing: "immediate" },
            { amount: 0, due_description: "Second payment", send_timing: "after_30" }
          ]).map((payment, idx) => (
            <div key={idx} className="p-3 bg-sand/30 rounded-lg space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-forest text-white flex items-center justify-center text-sm font-medium shrink-0">
                  {idx + 1}
                </div>
                <Input
                  value={payment.due_description}
                  onChange={(e) => {
                    const newPayments = [...(content.split_payments || [])];
                    newPayments[idx] = { ...newPayments[idx], due_description: e.target.value };
                    update({ split_payments: newPayments });
                  }}
                  placeholder="Description (e.g., Upon signing)"
                  className="flex-1"
                  disabled={readOnly}
                />
                <div className="relative w-32">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    value={payment.amount || ""}
                    onChange={(e) => {
                      const newPayments = [...(content.split_payments || [])];
                      newPayments[idx] = { ...newPayments[idx], amount: parseFloat(e.target.value) || 0 };
                      update({ split_payments: newPayments });
                    }}
                    className="pl-7"
                    disabled={readOnly}
                  />
                </div>
                {!readOnly && (content.split_payments || []).length > 2 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      const newPayments = (content.split_payments || []).filter((_, i) => i !== idx);
                      update({ split_payments: newPayments });
                    }}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                )}
              </div>
              
              {/* Send timing - only for payments after the first */}
              {idx > 0 && !readOnly && (
                <div className="pl-11">
                  <Label className="text-xs text-muted-foreground">When to send this payment request</Label>
                  <Select
                    value={payment.send_timing || "manual"}
                    onValueChange={(value) => {
                      const newPayments = [...(content.split_payments || [])];
                      newPayments[idx] = { ...newPayments[idx], send_timing: value };
                      update({ split_payments: newPayments });
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="after_30">30 days after previous payment</SelectItem>
                      <SelectItem value="after_45">45 days after previous payment</SelectItem>
                      <SelectItem value="after_60">60 days after previous payment</SelectItem>
                      <SelectItem value="after_90">90 days after previous payment</SelectItem>
                      <SelectItem value="manual">I'll send it manually</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {idx === 0 && !readOnly && (
                <p className="pl-11 text-xs text-muted-foreground">Sent immediately when prospect accepts</p>
              )}
            </div>
          ))}
          {!readOnly && (content.split_payments || []).length < 4 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const currentPayments = content.split_payments || [];
                const newPayments = [...currentPayments, { 
                  amount: 0, 
                  due_description: `Payment ${currentPayments.length + 1}`,
                  send_timing: "manual"
                }];
                update({ split_payments: newPayments });
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Payment
            </Button>
          )}
        </div>
      )}

      {/* SUBSCRIPTION */}
      {paymentType === "subscription" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium">Amount</Label>
              <div className="relative mt-2">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  value={content.subscription?.amount || ""}
                  onChange={(e) => {
                    update({ 
                      subscription: { 
                        ...content.subscription, 
                        amount: parseFloat(e.target.value) || 0 
                      } 
                    });
                  }}
                  className="pl-7"
                  disabled={readOnly}
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Frequency</Label>
              <Select
                value={content.subscription?.frequency || "monthly"}
                onValueChange={(value) => {
                  update({ 
                    subscription: { 
                      ...content.subscription, 
                      frequency: value 
                    } 
                  });
                }}
                disabled={readOnly}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annually">Annually</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium">Description (optional)</Label>
            <Input
              value={content.subscription?.description || ""}
              onChange={(e) => {
                update({ 
                  subscription: { 
                    ...content.subscription, 
                    description: e.target.value 
                  } 
                });
              }}
              placeholder="e.g., Ongoing advisory services"
              className="mt-2"
              disabled={readOnly}
            />
          </div>
        </div>
      )}

      {/* HYBRID (One-time + Subscription) */}
      {paymentType === "hybrid" && (
        <div className="space-y-6">
          {/* One-time fee */}
          <div className="p-4 border border-stone rounded-lg space-y-3">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-forest" />
              <Label className="text-sm font-medium">Setup / Onboarding Fee</Label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Amount</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    value={content.onboarding_fee || ""}
                    onChange={(e) => update({ onboarding_fee: parseFloat(e.target.value) || 0 })}
                    className="pl-7"
                    disabled={readOnly}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Description</Label>
                <Input
                  value={content.onboarding_description || ""}
                  onChange={(e) => update({ onboarding_description: e.target.value })}
                  placeholder="Onboarding fee"
                  className="mt-1"
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center">
            <ArrowRight className="w-5 h-5 text-muted-foreground" />
          </div>

          {/* Recurring subscription */}
          <div className="p-4 border border-stone rounded-lg space-y-3">
            <div className="flex items-center gap-2">
              <Repeat className="w-4 h-4 text-forest" />
              <Label className="text-sm font-medium">Recurring Subscription</Label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Amount</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    value={content.subscription?.amount || ""}
                    onChange={(e) => {
                      update({ 
                        subscription: { 
                          ...content.subscription, 
                          amount: parseFloat(e.target.value) || 0 
                        } 
                      });
                    }}
                    className="pl-7"
                    disabled={readOnly}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Frequency</Label>
                <Select
                  value={content.subscription?.frequency || "monthly"}
                  onValueChange={(value) => {
                    update({ 
                      subscription: { 
                        ...content.subscription, 
                        frequency: value 
                      } 
                    });
                  }}
                  disabled={readOnly}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annually">Annually</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Description</Label>
              <Input
                value={content.subscription?.description || ""}
                onChange={(e) => {
                  update({ 
                    subscription: { 
                      ...content.subscription, 
                      description: e.target.value 
                    } 
                  });
                }}
                placeholder="e.g., Monthly advisory fee"
                className="mt-1"
                disabled={readOnly}
              />
            </div>
            
            {/* Subscription Start Timing */}
            <div className="pt-3 border-t border-stone/50">
              <Label className="text-xs text-muted-foreground">When does subscription billing start?</Label>
              <Select
                value={content.subscription_start || "immediate"}
                onValueChange={(value) => update({ subscription_start: value })}
                disabled={readOnly}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Immediately with setup fee</SelectItem>
                  <SelectItem value="after_30">30 days after setup</SelectItem>
                  <SelectItem value="after_60">60 days after setup</SelectItem>
                  <SelectItem value="after_90">90 days after setup</SelectItem>
                  <SelectItem value="manual">I'll start it manually later</SelectItem>
                </SelectContent>
              </Select>
              {content.subscription_start === "manual" && (
                <p className="text-xs text-muted-foreground mt-2">
                  Only the setup fee will be charged now. You can start the subscription anytime from the proposal.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DELIVERY METHOD */}
      {!readOnly && (
        <div className="pt-4 border-t border-stone space-y-3">
          <Label className="text-sm font-medium">Delivery Method</Label>
          <RadioGroup
            value={deliveryMethod}
            onValueChange={(value) => update({ delivery_method: value })}
            className="flex gap-4"
          >
            <Label
              htmlFor="payment_link"
              className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer flex-1 transition-colors ${
                deliveryMethod === "payment_link" ? "border-forest bg-forest/5" : "border-stone hover:border-forest/50"
              }`}
            >
              <RadioGroupItem value="payment_link" id="payment_link" />
              <CreditCard className="w-4 h-4" />
              <div>
                <p className="font-medium text-sm">Payment Link</p>
                <p className="text-xs text-muted-foreground">Quick checkout page</p>
              </div>
            </Label>
            
            <Label
              htmlFor="invoice"
              className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer flex-1 transition-colors ${
                deliveryMethod === "invoice" ? "border-forest bg-forest/5" : "border-stone hover:border-forest/50"
              }`}
            >
              <RadioGroupItem value="invoice" id="invoice" />
              <FileText className="w-4 h-4" />
              <div>
                <p className="font-medium text-sm">Invoice</p>
                <p className="text-xs text-muted-foreground">Formal itemized invoice</p>
              </div>
            </Label>
          </RadioGroup>
        </div>
      )}

      {/* SUMMARY */}
      <div className="p-4 bg-forest/5 rounded-lg space-y-2">
        <p className="text-sm font-medium text-forest">Payment Summary</p>
        {calculateOneTimeTotal() > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">
              {paymentType === "split" ? "Total (split into payments)" : 
               paymentType === "hybrid" ? "Setup Fee" : "One-time Total"}
            </span>
            <span className="font-semibold">${calculateOneTimeTotal().toLocaleString()}</span>
          </div>
        )}
        {calculateRecurringTotal() > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Recurring</span>
            <span className="font-semibold">
              ${calculateRecurringTotal().toLocaleString()}
              {formatFrequency(content.subscription?.frequency)}
            </span>
          </div>
        )}
        {readOnly && (
          <div className="text-xs text-muted-foreground pt-2 border-t border-forest/10">
            {deliveryMethod === "invoice" ? "Will be sent as formal invoice" : "Will be sent as payment link"}
          </div>
        )}
      </div>
    </div>
  );
};

export default PricingSection;
